import React from 'react';
import { Typography, TypographyProps } from '@mui/material';
import { SxProps, Theme } from '@mui/system';

type CustomTextVariant = TypographyProps['variant'] | 'heading' | 'subHeading' | 'cardHeading' | 'cardValue';

export interface CustomTextProps extends Omit<TypographyProps, 'variant' | 'sx'> {
  variant?: CustomTextVariant;
  children: React.ReactNode;
  sx?: SxProps<Theme>;
}

const CustomText = React.forwardRef<HTMLSpanElement, CustomTextProps>(
  ({ variant = 'body1', children, sx, ...rest }, ref) => {
    let resolvedVariant: TypographyProps['variant'];
    switch (variant) {
      case 'heading':
        resolvedVariant = 'h4';
        break;
      case 'subHeading':
        resolvedVariant = 'subtitle1';
        break;
    case 'cardHeading':
        resolvedVariant = 'subtitle2';
        break;
    case 'cardValue':
      resolvedVariant = 'h5';
      break;
      default:
        resolvedVariant = variant as TypographyProps['variant'];
    }

    const baseHeadingSx: SxProps<Theme> = {
      color: '#1B1B1B',
      fontWeight: 600,
      fontSize: '32px',
      lineHeight: 1.25,
    };
    const baseSubHeadingSx: SxProps<Theme> = {
      color: '#767676',
      fontWeight: 400,
      fontSize: '16px',
      lineHeight: 1.5,
    };
    const baseCardHeadingSx: SxProps<Theme> = {
      color: '#333333',
      fontWeight: 400,
      fontSize: '14px',
      lineHeight: 1.5,
    };
    const baseCardValueSx: SxProps<Theme> = {
      color: '#333333',
      fontWeight: 600,
      fontSize: '26px',
      lineHeight: 1.3,
    };

    const presetSxMap: Record<'heading' | 'subHeading' | 'cardHeading' | 'cardValue', SxProps<Theme>> = {
      heading: baseHeadingSx,
      subHeading: baseSubHeadingSx,
      cardHeading: baseCardHeadingSx,
      cardValue: baseCardValueSx,
    };

    const mergedSx: SxProps<Theme> = [
       ...(variant in presetSxMap ? [presetSxMap[variant as 'heading' | 'subHeading' | 'cardHeading' | 'cardValue']] : []),
      ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
    ];

    return (
      <Typography ref={ref} variant={resolvedVariant} sx={mergedSx} {...rest}>
        {children}
      </Typography>
    );
  },
);

CustomText.displayName = 'CustomText';

export default CustomText;
